/**
 * SearchField - Search input with search icon
 *
 * Use with React Hook Form:
 * <SearchField label="Search" {...register('search')} />
 *
 * Or standalone:
 * <SearchField label="Search" value={query} onChange={(e) => setQuery(e.target.value)} />
 */

import { Search } from 'lucide-react';
import { forwardRef } from 'react';

import { BaseInput, type BaseInputProps } from '../base';

export interface SearchFieldProps extends Omit<BaseInputProps, 'type' | 'iconLeft' | 'iconRight'> {
  /** Field label */
  label: string;
  /** Hide the label visually (still accessible to screen readers) */
  hideLabel?: boolean;
}

export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(
  ({ hideLabel, label, className, ...props }, ref) => (
    <div className={className}>
      {hideLabel ? (
        <label className="sr-only">{label}</label>
      ) : (
        <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300">
          {label}
        </label>
      )}
      <BaseInput
        ref={ref}
        type="search"
        iconLeft={<Search className="h-4 w-4" />}
        className={hideLabel ? '' : 'mt-1'}
        {...props}
      />
    </div>
  )
);

SearchField.displayName = 'SearchField';
